import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HomePage, LoginPage, ConsentPage, StudentInterface, ExamInterface, ExamCompletePage, InstructorDashboard } from '@/components';
import { Toaster } from '@/components/ui';
import { useAuthStore } from '@/stores';
import { initializeStores } from './stores';
import { Loader2 } from 'lucide-react';

// Protected Route Component to prevent navigation loops
const ProtectedRoute: React.FC<{
  role: 'student' | 'teacher';
  component: React.ReactElement;
  requiresConsent?: boolean;
}> = ({ role, component, requiresConsent = false }) => {
  const { isAuthenticated, user } = useAuthStore();
  const redirectHandled = useRef(false);

  // Reset redirect flag on route change
  useEffect(() => {
    redirectHandled.current = false;
  }, []);

  if (!isAuthenticated || !user) {
    if (!redirectHandled.current) {
      redirectHandled.current = true;
      return <Navigate to="/login" replace />;
    }
    return null;
  }

  if (user.role !== role) {
    if (!redirectHandled.current) {
      redirectHandled.current = true;
      return <Navigate to="/login" replace />;
    }
    return null;
  }

  // Check consent requirement for student routes
  if (requiresConsent && role === 'student') {
    const consentGiven = localStorage.getItem('consent_given') === 'true';
    if (!consentGiven) {
      if (!redirectHandled.current) {
        redirectHandled.current = true;
        return <Navigate to="/consent" replace />;
      }
      return null;
    }
  }

  return component;
};

function App() {
  const [storesInitialized, setStoresInitialized] = useState(false);

  useEffect(() => {
    // Initialize all stores when the app starts
    const cleanup = initializeStores();
    setStoresInitialized(true);

    // Cleanup when component unmounts
    return cleanup;
  }, []);

  // Don't render routes until stores are initialized to prevent navigation loops
  if (!storesInitialized) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-canvas text-content-primary">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 size-8 animate-spin text-ink" aria-hidden="true" />
          <p className="font-display text-heading-sm font-normal">Initializing QuizSecure...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-surface-canvas text-content-primary">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />

          {/* Protected Routes */}
          <Route
            path="/consent"
            element={<ProtectedRoute role="student" component={<ConsentPage />} />}
          />
          <Route
            path="/student"
            element={<ProtectedRoute role="student" component={<StudentInterface />} requiresConsent={true} />}
          />
          <Route
            path="/exam"
            element={<ProtectedRoute role="student" component={<ExamInterface />} requiresConsent={true} />}
          />
          <Route
            path="/exam-complete"
            element={<ProtectedRoute role="student" component={<ExamCompletePage />} requiresConsent={true} />}
          />
          <Route
            path="/instructor"
            element={<ProtectedRoute role="teacher" component={<InstructorDashboard />} />}
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster />
      </div>
    </Router>
  );
}

export default App;
